import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { EMPTY, catchError, from, takeUntil } from 'rxjs'
import { finalize, switchMap, tap } from 'rxjs/operators'
import { ProfileApi, VideoApi } from 'src/api'
import { BreadcrumbHeading, InputDebounce, TabsNav, useAnalytic } from 'src/components'
import { ETrackingEvent } from 'src/enums'
import { useDebounceAnalyticInput, useQueryParams, useUnsubscribe } from 'src/hooks'
import { IconSearch } from 'src/icons'
import { IVideoModel } from 'src/interfaces'
import { Breadcrumbs } from 'src/layouts/breadcrumbs'
import { ERoutes, generate } from 'src/router'
import { SnackbarService } from 'src/services'
import { AuthModule, LoadingModule } from 'src/store'
import { VideosArchive } from '../archive'
import { Empty } from '../empty'
import { VideoPublished } from '../published'

interface IProps {
  onSelectVideo?: (video?: IVideoModel) => void
}

type TTab = 'published' | 'archive'

const SCREEN = 'videos'

export const Videos: FC<IProps> = () => {
  const history = useHistory()
  const { tab } = useQueryParams()
  const unsubscribe$ = useUnsubscribe()

  const [tabDefault, setTabDefault] = useState<TTab>('published')
  const [counts, setCounts] = useState<Awaited<ReturnType<typeof VideoApi.count>>['data']>()
  const [keyword, setKeyword] = useState<string>()
  const [loading, setLoading] = useState(false)
  const { analytic } = useAnalytic(SCREEN)
  const { analyticInput } = useDebounceAnalyticInput('top_nav')

  const onSearch = (value?: string) => {
    setKeyword(value)
    analyticInput(ETrackingEvent.INPUT_NAV_SEARCH_VIDEOS, value)
  }

  const loadCounts = useCallback(() => {
    LoadingModule.toggle(true)
    from(VideoApi.count())
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => LoadingModule.toggle(false))
      )
      .subscribe(({ data }) => setCounts(data))
  }, [unsubscribe$])

  const onUpdateProfile = useCallback(async (pfvId: number, fnCallback?: () => void) => {
    from(ProfileApi.update({ pfvId }))
      .pipe(
        switchMap(() => ProfileApi.getProfile()),
        tap(({ data }) => AuthModule.updateProfile(data)),
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => setLoading(false))
      )
      .subscribe(() => {
        loadCounts()
        fnCallback?.()
      })
  }, [unsubscribe$, loadCounts])

  useEffect(() => {
    loadCounts()
  }, [loadCounts])

  useEffect(() => {
    if (['published', 'archive'].includes(tab as TTab)) {
      setTabDefault(tab as TTab)
    } else {
      history.push(generate(ERoutes.MY_VIDEOS, { tab: 'published' }))
    }
  }, [history, tab])

  const items = useMemo(() => {
    return [
      {
        key: 'published',
        label: 'Published',
        children: <VideoPublished keyword={keyword} screen={SCREEN} onUpdateProfile={onUpdateProfile}/>
      },
      {
        key: 'archive',
        label: `Archive (${counts?.archived || 0})`,
        children: <VideosArchive keyword={keyword} screen={SCREEN} onUpdateProfile={onUpdateProfile}/>
      }
    ]
  }, [counts, keyword, onUpdateProfile])

  return (
    <>
      <Breadcrumbs>
        <div className="fx fx-ai-center gap-6">
          <BreadcrumbHeading active>My Videos</BreadcrumbHeading>
          <InputDebounce
            prefix={<IconSearch size={20}/>}
            style={{ width: 350 }}
            placeholder="Search jobs, skills, locations…"
            value={keyword}
            onChange={onSearch}
          />
        </div>
      </Breadcrumbs>

      {!counts?.public
        ? (
          <Empty loading={loading} screen={SCREEN} onUpdateProfile={onUpdateProfile}/>
        )
        : (
          <TabsNav
            className="fx-1 fx-column gap-8 p-8"
            defaultActiveKey={tabDefault}
            items={items}
            onChange={(tab) => {
              if (tab === 'published') {
                analytic(ETrackingEvent.BTN_NAV_MY_VIDEOS_PUBLISHED)
              } else {
                analytic(ETrackingEvent.BTN_NAV_MY_VIDEOS_ARCHIVE)
              }

              history.push(generate(ERoutes.MY_VIDEOS, { tab }))
            }}
          />
        )}

    </>
  )
}
